import type { LiveWorkingQueueItem, Message, TimelineEntry } from './hooks/useChatMessageFormatting'

export function timelineHasToolCall(entries: TimelineEntry[] | undefined): boolean {
  return (entries ?? []).some((e) => e.kind === 'tool_call')
}

function hasToolStarted(
  msg: Pick<Message, 'liveLeadMarkdown' | 'liveWorkingQueue' | 'timeline'>,
  entries?: TimelineEntry[],
): boolean {
  if ((msg.liveWorkingQueue ?? []).some((w) => w.kind === 'tool')) {
    return true
  }
  return timelineHasToolCall(entries ?? msg.timeline?.entries)
}

/** Promote streamed text into preamble lead before the first tool (step or tool boundary). */
export function flushPreToolPreambleLanes(
  msg: Pick<Message, 'liveLeadMarkdown' | 'liveWorkingQueue' | 'timeline'>,
  processedStream: string,
  entries?: TimelineEntry[],
): {liveLeadMarkdown: string; liveWorkingQueue: LiveWorkingQueueItem[]} {
  if (hasToolStarted(msg, entries)) {
    return {
      liveLeadMarkdown: msg.liveLeadMarkdown ?? '',
      liveWorkingQueue: msg.liveWorkingQueue ?? [],
    }
  }
  return flushStreamingBeforeTool(msg, processedStream)
}

/** Flush streamed text into lead (before any tool) or working reasoning (after ≥1 tool). */
export function flushStreamingBeforeTool(
  msg: Pick<Message, 'liveLeadMarkdown' | 'liveWorkingQueue'>,
  processedStream: string,
): { liveLeadMarkdown: string; liveWorkingQueue: LiveWorkingQueueItem[] } {
  const queue = [...(msg.liveWorkingQueue ?? [])]
  let lead = msg.liveLeadMarkdown ?? ''
  const toolsBefore = queue.filter((w) => w.kind === 'tool').length
  const p = processedStream.trim()
  if (p) {
    if (toolsBefore === 0) {
      lead = lead ? `${lead}\n\n${p}` : p
    } else {
      queue.push({ kind: 'reasoning', markdown: p })
    }
  }
  return {liveLeadMarkdown: lead, liveWorkingQueue: queue}
}

/** Apply a tool-call-start: freeze streaming buffer into lanes, append tool placeholder. */
export function applyToolCallStartToLiveLanes(
  msg: Pick<Message, 'liveLeadMarkdown' | 'liveWorkingQueue'>,
  toolCallId: string,
  processedStream: string,
): { liveLeadMarkdown: string; liveWorkingQueue: LiveWorkingQueueItem[] } {
  const flushed = flushStreamingBeforeTool(msg, processedStream)
  return {
    liveLeadMarkdown: flushed.liveLeadMarkdown,
    liveWorkingQueue: [...flushed.liveWorkingQueue, { kind: 'tool', toolCallId }],
  }
}
